import { createEntityAdapter, EntityState } from '@reduxjs/toolkit'
import { matchTrackerApi } from '../../modules/servers/api'
import { createCrudEndpoints } from '../api'
import { RawTeamData } from '../teams/types'
import { RawTeamPersonData } from './types'

const tagType = 'teampeople' as const

export const teamPeopleApi = matchTrackerApi.injectEndpoints({
  endpoints: (builder) => ({
    // Generic CRUD endpoints //
    ...createCrudEndpoints<RawTeamPersonData>(tagType, builder),

    // Get all team people for a specific team //
    getTeamPeopleByTeamId: builder.query<
      EntityState<RawTeamPersonData, string>,
      { teamId: string; includeInactive?: boolean }
    >({
      extraOptions: { isListWithEmpty404Response: true },
      // includeInactive is sent as a query parameter (never appended to the url),
      // so the resource path stays `/teams/{teamId}` and the two variants remain
      // distinct RTK cache entries keyed by the query argument.
      query: ({ teamId, includeInactive }) => ({
        url: `/teams/${teamId}`,
        method: 'GET',
        withCredentials: true,
        ...(includeInactive ? { params: { includeInactive: true } } : {})
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.ids.map((id) => ({ type: tagType, id })),
              { type: tagType, id: 'LIST' }
            ]
          : [{ type: tagType, id: 'LIST' }],
      transformResponse: (response: RawTeamData) => {
        const teamPeople = response.teamPeople || []
        const entityAdapter = createEntityAdapter<RawTeamPersonData>()
        try {
          console.log('Transforming response for', tagType, teamPeople)
          return entityAdapter.addMany(
            entityAdapter.getInitialState(),
            teamPeople
          )
        } catch (error) {
          console.error(`Error in transformResponse for ${tagType}:`, error)
          throw error
        }
      }
    }),

    // Get all team people for a specific person //
    getTeampeopleByPersonId: builder.query<
      EntityState<RawTeamPersonData, string>,
      { organisationId: string | null; personId: string }
    >({
      extraOptions: { isListWithEmpty404Response: true },
      query: (arg) => {
        return {
          url: `people/${arg.personId}/teams`,
          params: {
            orgId: arg.organisationId,
            join: true
          }
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.ids.map((id) => ({ type: tagType, id })),
              { type: tagType, id: 'LIST' }
            ]
          : [{ type: tagType, id: 'LIST' }],
      transformResponse: (response: RawTeamPersonData[]) => {
        const entityAdapter = createEntityAdapter<RawTeamPersonData>()
        return entityAdapter.addMany(entityAdapter.getInitialState(), response)
      }
    })
  })
})

export const {
  useTeampeople_getAllQuery,
  useTeampeople_getByIdQuery,
  useTeampeople_createMutation,
  useTeampeople_updateMutation,
  useTeampeople_deleteMutation,
  useGetTeamPeopleByTeamIdQuery,
  useGetTeampeopleByPersonIdQuery
} = teamPeopleApi
